import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Table, Button, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import { fetchAdminDashboard } from '../../api/adminDashboard';
import { useLanguage } from '../../context/LanguageProvider/LanguageProvider';
import { useAdminData } from '../../context/AdminDataProvider/AdminDataProvider';
import { LOCAL_CONSUMPTION_BY_COUNTRY } from '../../data/adminConsumptionFallback';

const STATUS_BADGE = {
  active: 'bg-success',
  in_progress: 'bg-primary',
  completed: 'bg-secondary',
};

function formatConsumptionValue(value, locale) {
  return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US').format(value);
}

export default function AdminDashboard() {
  const { t, locale } = useLanguage();
  const { projects, users, domains } = useAdminData();

  const [apiLoading, setApiLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [apiPayload, setApiPayload] = useState(null);

  useEffect(() => {
    const ac = new AbortController();
    setApiLoading(true);
    setApiError(null);
    fetchAdminDashboard(ac.signal)
      .then((data) => {
        setApiPayload(data);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setApiPayload(null);
        setApiError(err.message || 'fetch failed');
      })
      .finally(() => {
        setApiLoading(false);
      });
    return () => ac.abort();
  }, []);

  const apiConnected = Boolean(apiPayload?.ok);
  const useRemote = Boolean(apiConnected && !apiPayload?.empty);

  const localRecentRows = useMemo(() => projects.slice(0, 5), [projects]);

  const localActiveProjectCount = useMemo(
    () => projects.filter((p) => (p.status || 'active') === 'active').length,
    [projects],
  );

  const localDomainRows = useMemo(
    () =>
      domains.map((d) => ({
        ...d,
        projectCount: projects.filter((p) => p.domainId === d.id).length,
      })),
    [domains, projects],
  );

  const recentRows = useRemote ? apiPayload.recentProjects : localRecentRows;
  const domainRows = useRemote ? apiPayload.domainRows : localDomainRows;

  const consumptionFromBackend = Boolean(
    apiConnected && Array.isArray(apiPayload?.consumptionByCountry) && apiPayload.consumptionByCountry.length > 0,
  );

  const consumptionRows = useMemo(() => {
    if (consumptionFromBackend) {
      return apiPayload.consumptionByCountry;
    }
    return LOCAL_CONSUMPTION_BY_COUNTRY;
  }, [consumptionFromBackend, apiPayload]);

  const consumptionTotal = useMemo(
    () => consumptionRows.reduce((sum, row) => sum + row.value, 0),
    [consumptionRows],
  );

  const maxConsumption = useMemo(
    () => Math.max(...consumptionRows.map((row) => row.value), 1),
    [consumptionRows],
  );

  const kpiItems = useMemo(() => {
    if (useRemote) {
      const s = apiPayload.stats;
      return [
        { labelKey: 'admin.kpi.projects', value: String(s.projectCount), tone: 'primary' },
        { labelKey: 'admin.kpi.users', value: String(s.userCount), tone: 'success' },
        { labelKey: 'admin.kpi.domains', value: String(s.domainCount), tone: 'info' },
        { labelKey: 'admin.kpi.activeProjects', value: String(s.activeProjectCount), tone: 'warning' },
      ];
    }
    return [
      { labelKey: 'admin.kpi.projects', value: String(projects.length), tone: 'primary' },
      { labelKey: 'admin.kpi.users', value: String(users.length), tone: 'success' },
      { labelKey: 'admin.kpi.domains', value: String(domains.length), tone: 'info' },
      { labelKey: 'admin.kpi.activeProjects', value: String(localActiveProjectCount), tone: 'warning' },
    ];
  }, [useRemote, apiPayload, projects.length, users.length, domains.length, localActiveProjectCount]);

  return (
    <div className="jassp-admin-dashboard">
      {apiLoading ? (
        <div className="d-flex align-items-center gap-2 text-muted small mb-3">
          <Spinner animation="border" size="sm" role="status" aria-hidden />
          <span>{t('admin.dashboard.apiConnecting')}</span>
        </div>
      ) : null}

      {!apiLoading && apiError ? (
        <Alert variant="warning" className="mb-3 py-2 small">
          {t('admin.dashboard.backendError')}
        </Alert>
      ) : null}

      {!apiLoading && apiPayload?.ok && apiPayload.empty ? (
        <Alert variant="info" className="mb-3 py-2 small">
          {t('admin.dashboard.backendEmpty')}
        </Alert>
      ) : null}

      {!apiLoading && useRemote ? (
        <Alert variant="success" className="mb-3 py-2 small border-0 bg-success bg-opacity-10 text-success">
          {t('admin.dashboard.backendLive')}
        </Alert>
      ) : null}

      <Row className="g-3 g-lg-4 mb-4">
        {kpiItems.map((kpi) => (
          <Col key={kpi.labelKey} xs={12} sm={6} xl={3}>
            <Card className="border-0 shadow-sm h-100 jassp-admin-kpi">
              <Card.Body className="p-4">
                <p className="text-muted small mb-1 text-uppercase fw-semibold">{t(kpi.labelKey)}</p>
                <p className={`display-6 fw-bold mb-0 text-${kpi.tone}`}>{kpi.value}</p>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="g-3 g-lg-4 mb-4">
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
              <span className="fw-semibold mb-0">{t('admin.dashboard.domainsTitle')}</span>
              <Button as={Link} to="/admin/domains" variant="outline-primary" size="sm" className="rounded-pill">
                {t('admin.dashboard.manageDomains')}
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4">{t('admin.crud.colName')}</th>
                    <th>{t('admin.crud.colSlug')}</th>
                    <th className="text-end pe-4">{t('admin.dashboard.projectsInDomain')}</th>
                  </tr>
                </thead>
                <tbody>
                  {domainRows.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center text-muted py-4 px-4">
                        {t('admin.dashboard.noDomains')}
                      </td>
                    </tr>
                  ) : (
                    domainRows.map((d) => (
                      <tr key={d.id}>
                        <td className="ps-4">{d.name}</td>
                        <td>
                          <code className="small">{d.slug}</code>
                        </td>
                        <td className="text-end pe-4">{d.projectCount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 g-lg-4 mb-4">
        <Col lg={7}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-3 fw-semibold">{t('admin.recent.title')}</Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4">{t('admin.recent.col.project')}</th>
                    <th className="pe-4 text-end">{t('admin.recent.col.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRows.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="text-center text-muted py-4 px-4">
                        {t('admin.recent.empty')}
                      </td>
                    </tr>
                  ) : (
                    recentRows.map((row) => (
                      <tr key={row.id}>
                        <td className="ps-4">{row.name}</td>
                        <td className="pe-4 text-end">
                          <span
                            className={`badge text-white ${STATUS_BADGE[row.status] || STATUS_BADGE.active}`}
                          >
                            {t(`admin.crud.projectStatus.${row.status || 'active'}`)}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
            <Card.Footer className="bg-white border-top py-2 text-end">
              <Link to="/admin/projects" className="small text-decoration-none">
                {t('admin.dashboard.seeAllProjects')}
              </Link>
            </Card.Footer>
          </Card>
        </Col>
        <Col lg={5}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-3 fw-semibold">{t('admin.activity.title')}</Card.Header>
            <Card.Body className="p-0">
              <ul className="list-unstyled mb-0 jassp-admin-feed">
                {['admin.activity.a1', 'admin.activity.a2', 'admin.activity.a3'].map((key) => (
                  <li key={key} className="px-4 py-3 border-bottom border-light">
                    <span className="jassp-admin-feed-dot me-2" />
                    <span className="fs-14 text-gray-700">{t(key)}</span>
                  </li>
                ))}
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 g-lg-4">
        <Col lg={8}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-3">
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                <div>
                  <div className="fw-semibold">{t('admin.consumption.title')}</div>
                  <small className="text-muted">{t('admin.consumption.subtitle')}</small>
                </div>
                {consumptionFromBackend ? (
                  <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">
                    {t('admin.consumption.backendLive')}
                  </span>
                ) : null}
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              {!apiLoading && apiError ? (
                <Alert variant="warning" className="py-2 small mb-3">
                  {t('admin.consumption.backendError')}
                </Alert>
              ) : null}
              <p className="text-muted small mb-4">
                {t('admin.consumption.total')}{' '}
                <span className="fw-semibold text-dark">
                  {formatConsumptionValue(consumptionTotal, locale)}
                </span>{' '}
                {t('admin.consumption.unit')}
              </p>
              <ul className="list-unstyled mb-0 jassp-admin-consumption-list">
                {consumptionRows.map((row) => (
                  <li key={row.countryCode} className="jassp-admin-consumption-item mb-3">
                    <div className="d-flex justify-content-between align-items-baseline gap-2 mb-1">
                      <span className="fw-semibold text-dark">{row.countryName}</span>
                      <span className="small text-muted text-nowrap">
                        {formatConsumptionValue(row.value, locale)} ({row.percent}%)
                      </span>
                    </div>
                    <div className="jassp-admin-consumption-track rounded-pill">
                      <div
                        className="jassp-admin-consumption-bar rounded-pill"
                        style={{ width: `${(row.value / maxConsumption) * 100}%` }}
                        role="presentation"
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-3 fw-semibold">{t('admin.quick.title')}</Card.Header>
            <Card.Body className="d-grid gap-2 p-3">
              <Button as={Link} to="/admin/projects" variant="outline-primary" className="text-start">
                {t('admin.quick.newProject')}
              </Button>
              <Button as={Link} to="/admin/users" variant="outline-primary" className="text-start">
                {t('admin.quick.addUser')}
              </Button>
              <Button as={Link} to="/admin/domains" variant="outline-primary" className="text-start">
                {t('admin.nav.domains')}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
